import SubHeaders from '@/components/Headers/SubHeaders'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import BriefCaseIcons from '@/icons/BriefCaseIcons'
import CelebrationIcons from '@/icons/CelebrationIcons'
import NavigateAfterIcons from '@/icons/NavigateAfterIcons'
import NavigateBeforeIcons from '@/icons/NavigateBeforeIcons'
import SettingsRemoteIcons from '@/icons/SettingsRemoteIcons'
import SnowshoeingIcons from '@/icons/SnowshoeingIcons'
import StarHalfIcons from '@/icons/StarHalfIcons'
import StarIcons from '@/icons/StarIcons'
import UnpaidIcons from '@/icons/UnpaidIcons'
import useAuthStore from '@/stores/useAuthStore'
import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, FormItem, IconButton, Tooltip } from 'vnetwork-ui'
import {
    calculateLateMinutes,
    formatLateMinutes,
    formatWorkDayTime,
    getWorkDayTooltipTitle,
    getWorkDayTypeColor,
    WEEKDAYS,
} from '../../constants'
import {
    getTotalWorkDaysByEmployeeID,
    getWorkDayByEmployeeID,
} from '../../services'
import { WorkDay } from '../../types/response'
import WorkDayTime from './WorkDayTime'

interface WorkDayEmployeeProps extends IPermissionProps {
    hideHeader?: boolean
}

const WorkDayEmployee: React.FC<WorkDayEmployeeProps> = ({ hideHeader }) => {
    const { t } = useTranslation()
    const { employee } = useAuthStore()
    const [hoveredWorkDay, setHoveredWorkDay] = useState<number | null>(null)
    const [hoveredDate, setHoveredDate] = useState<string | null>(null)
    const navigate = useNavigate()

    const handleMouseEnterDate = (dateStr: string) => {
        setHoveredDate(dateStr)
    }

    const handleMouseLeaveDate = () => {
        setHoveredDate(null)
    }

    const handleCreateLeave = (date: dayjs.Dayjs) => {
        navigate('/account/dayoffs/new', {
            state: { startDate: date.toISOString() },
        })
    }

    const handleCreateBusiness = (date: dayjs.Dayjs) => {
        navigate('/account/businesses/new', {
            state: { startDate: date.toISOString() },
        })
    }

    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const years = Array.from(
        { length: currentYear + 2 - 2000 + 1 },
        (_, index) => currentYear + 2 - index
    )

    const months = Array.from({ length: 12 }, (_, index) => index + 1)

    const [year, setYear] = useState(currentYear)
    const [month, setMonth] = useState(currentMonth)

    const {
        data: totalworkday,
        refetch: refetchWorkday,
        renderState: renderTotalWorkdayState,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WORKDAYS_TOTAL, year, month],
        queryFn: async () => {
            const result = await getTotalWorkDaysByEmployeeID({
                employee_id: employee?.employee_id || 0,
                year,
                month,
            })
            return result
        },
    })

    const {
        data: workdays,
        refetch: refetchWorkdays,
        renderState: renderWorkdaysState,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WORKDAYS, year, month],
        queryFn: async () => {
            const result = await getWorkDayByEmployeeID({
                page: 1,
                page_size: 40,
                year,
                month,
            })
            return result
        },
    })

    const { control, setValue } = useForm({
        defaultValues: {
            year: currentYear,
            month: currentMonth,
        },
    })

    const standardWorkdays = useMemo(() => {
        const startOfMonth = dayjs()
            .year(year)
            .month(month - 1)
            .startOf('month')
        const endOfMonth = dayjs()
            .year(year)
            .month(month - 1)
            .endOf('month')
        let count = 0

        for (
            let day = startOfMonth;
            day.isBefore(endOfMonth.add(1, 'day'), 'day');
            day = day.add(1, 'day')
        ) {
            if (day.day() !== 0) {
                count++
            }
        }

        return count
    }, [year, month])

    const totalActualWorkHours = useMemo(() => {
        if (!workdays?.results) return 0
        return workdays.results.reduce((total, workDay) => {
            return total + (workDay.work_day_time || 0)
        }, 0)
    }, [workdays])

    const totalLateMinutes = useMemo(() => {
        if (!workdays?.results) return 0
        return workdays.results.reduce((total, workDay) => {
            const dayOfWeek = dayjs(workDay.work_day_date).day()
            return (
                total +
                calculateLateMinutes(
                    workDay.work_day_in,
                    workDay.work_day_option,
                    dayOfWeek
                )
            )
        }, 0)
    }, [workdays])

    const getLateMinutesForWorkDay = (workDay: WorkDay): number => {
        const dayOfWeek = dayjs(workDay.work_day_date).day()
        return calculateLateMinutes(
            workDay.work_day_in,
            workDay.work_day_option,
            dayOfWeek
        )
    }

    useEffect(() => {
        refetchWorkday()
        refetchWorkdays()
    }, [year, month, refetchWorkday, refetchWorkdays])

    const calendarDays = useMemo(() => {
        const startOfMonth = dayjs()
            .year(year)
            .month(month - 1)
            .startOf('month')
        const endOfMonth = dayjs()
            .year(year)
            .month(month - 1)
            .endOf('month')
        const days = []
        let current = startOfMonth

        while (current.isBefore(endOfMonth.add(1, 'day'))) {
            days.push(current)
            current = current.add(1, 'day')
        }

        return days
    }, [year, month])

    const getWorkDayForDate = (date: dayjs.Dayjs): WorkDay | undefined => {
        return workdays?.results?.find(
            (wd) =>
                dayjs(wd.work_day_date).format('YYYY-MM-DD') ===
                date.format('YYYY-MM-DD')
        )
    }

    const handlePreviousMonth = () => {
        if (month === 1) {
            setYear(year - 1)
            setMonth(12)
            setValue('year', year - 1)
            setValue('month', 12)
        } else {
            setMonth(month - 1)
            setValue('month', month - 1)
        }
    }

    const handleNextMonth = () => {
        if (month === 12) {
            setYear(year + 1)
            setMonth(1)
            setValue('year', year + 1)
            setValue('month', 1)
        } else {
            setMonth(month + 1)
            setValue('month', month + 1)
        }
    }

    const getWorkDayTypeIcon = (type: string, workNDay: number) => {
        switch (type) {
            case 'Ngày bình thường':
                if (workNDay === 1) {
                    return <StarIcons className="w-4 h-4 text-yellow-400" />
                } else if (workNDay === 0.5) {
                    return <StarHalfIcons className="w-4 h-4 text-yellow-400" />
                } else {
                    return ''
                }
            case 'Nghỉ phép năm':
                return <SnowshoeingIcons className="w-4 h-4 text-blue-500" />
            case 'Nghỉ không phép':
                if (workNDay === 0) {
                    return <UnpaidIcons className="w-4 h-4 text-red-400" />
                } else if (workNDay === 0.5) {
                    return <StarHalfIcons className="w-4 h-4 text-yellow-400" />
                } else {
                    return ''
                }
            case 'Ngày làm việc remote':
                return (
                    <SettingsRemoteIcons className="w-4 h-4 text-green-400" />
                )
            case 'Ngày nghỉ lễ, tết':
                return <CelebrationIcons className="w-4 h-4 text-orange-500" />
            case 'Ngày công tác':
                return <BriefCaseIcons className="w-4 h-4 text-red-400" />
            default:
                return ''
        }
    }

    const totalWorkdayStateComponent = renderTotalWorkdayState()
    if (totalWorkdayStateComponent) return totalWorkdayStateComponent

    const workdaysStateComponent = renderWorkdaysState()
    if (workdaysStateComponent) return workdaysStateComponent

    return (
        <div>
            {!hideHeader && (
                <SubHeaders
                    actions={[]}
                    breadcrumbs={[
                        {
                            path: paths.workdayList,
                            title: t('workday-list'),
                        },
                    ]}
                />
            )}
            <div className="grid grid-cols-2 mt-2 gap-2">
                <div className="bg-white rounded-[8px] p-4">
                    <div className="text-sm text-primary">
                        {t('workday-total')} {month}/{year}
                    </div>
                    <div className="text-xl font-semibold text-black mt-1">
                        {totalworkday?.result?.total_work_days_in_month ?? 0}
                        <span className="text-sm font-normal text-gray-400">
                            {' '}
                            / {standardWorkdays}
                        </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1 mt-2">
                        <div
                            className="bg-primary h-1 rounded-full transition-all"
                            style={{
                                width: `${Math.min(
                                    ((totalworkday?.result
                                        ?.total_work_days_in_month ?? 0) /
                                        standardWorkdays) *
                                        100,
                                    100
                                )}%`,
                            }}
                        />
                    </div>
                </div>

                <div className="bg-white rounded-[8px] p-4">
                    <div className="text-sm text-primary">
                        {t('total-actual-work-hours')}
                    </div>
                    <div className="text-xl font-semibold text-black mt-1">
                        {formatWorkDayTime(totalActualWorkHours) || '0'}
                        <span className="text-sm font-normal text-gray-400">
                            {' '}
                            giờ
                        </span>
                    </div>
                    {totalLateMinutes > 0 && (
                        <div className="text-error text-xs mt-1">
                            {t('total-late-minutes')}:{' '}
                            {formatLateMinutes(totalLateMinutes)}
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-2 p-4 bg-white rounded-[8px]">
                <div className="flex gap-4 mb-4 items-end">
                    <FormItem
                        name="year"
                        control={control}
                        type="select"
                        label={t('year')}
                        placeholder={t('year-placeholder')}
                        options={years.map((yr) => ({
                            label: `${t('year')} ${yr}`,
                            value: yr,
                        }))}
                        onChangeAfterHookForm={(e) => setYear(Number(e))}
                    />

                    <FormItem
                        name="month"
                        control={control}
                        type="select"
                        label={t('month')}
                        placeholder={t('month-placeholder')}
                        options={months.map((mon) => ({
                            label: `${t('month')} ${mon}`,
                            value: mon,
                        }))}
                        onChangeAfterHookForm={(e) => setMonth(Number(e))}
                    />
                    <div className="flex gap-2 ml-auto">
                        <IconButton size="small" onClick={handlePreviousMonth}>
                            <NavigateBeforeIcons className="m-[-2px]" />
                        </IconButton>
                        <IconButton size="small" onClick={handleNextMonth}>
                            <NavigateAfterIcons className="m-[-2px]" />
                        </IconButton>
                    </div>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                    {WEEKDAYS.map((day) => (
                        <div
                            key={day}
                            className="p-2 text-center font-semibold text-black bg-gray-50"
                        >
                            {day}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: calendarDays[0]?.day() || 0 }).map(
                        (_, index) => (
                            <div
                                key={`empty-${index}`}
                                className="min-h-[80px] bg-[#F9FBFF] border rounded"
                            ></div>
                        )
                    )}
                    {calendarDays.map((day, index) => {
                        const workDay = getWorkDayForDate(day)
                        const isToday = day.isSame(dayjs(), 'day')

                        return (
                            <div
                                key={index}
                                className={`min-h-[80px] p-3 border rounded relative group ${
                                    isToday ? 'ring-2 ring-blue-500' : ''
                                } ${!workDay ? 'bg-[#F9FBFF]' : 'bg-white'}`}
                                onMouseEnter={() =>
                                    handleMouseEnterDate(
                                        day.format('YYYY-MM-DD')
                                    )
                                }
                                onMouseLeave={handleMouseLeaveDate}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="text-sm font-medium text-gray-900">
                                        {day.format('D')}
                                    </div>
                                    {workDay && (
                                        <Tooltip
                                            title={getWorkDayTooltipTitle(
                                                workDay.work_day_type,
                                                workDay.work_day_option
                                            )}
                                            placement="top"
                                        >
                                            <div className="text-lg cursor-pointer">
                                                {getWorkDayTypeIcon(
                                                    workDay.work_day_type,
                                                    workDay.work_n_day
                                                )}
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>

                                {/* Create Options on Hover */}
                                {hoveredDate === day.format('YYYY-MM-DD') &&
                                    !workDay && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/5 rounded animate-in fade-in duration-200 gap-1">
                                            <Tooltip title={t('create-leave')}>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    className="bg-white hover:bg-gray-100 shadow-sm"
                                                    onClick={() =>
                                                        handleCreateLeave(day)
                                                    }
                                                >
                                                    <SnowshoeingIcons className="w-4 h-4 text-blue-500" />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip
                                                title={t('create-business')}
                                            >
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    className="bg-white hover:bg-gray-100 shadow-sm"
                                                    onClick={() =>
                                                        handleCreateBusiness(
                                                            day
                                                        )
                                                    }
                                                >
                                                    <BriefCaseIcons className="w-4 h-4 text-red-400" />
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    )}

                                {workDay && (
                                    <div className="mt-1 text-center">
                                        {workDay.work_day_time !== 0 &&
                                            workDay.work_day_time !== null && (
                                                <div className="relative">
                                                    <div
                                                        className={`text-xs text px-2 py-1 rounded cursor-pointer ${getWorkDayTypeColor(
                                                            workDay.work_day_type,
                                                            workDay.work_day_in,
                                                            workDay.work_day_option
                                                        )}`}
                                                        onMouseEnter={() =>
                                                            setHoveredWorkDay(
                                                                workDay.work_day_id
                                                            )
                                                        }
                                                        onMouseLeave={() =>
                                                            setHoveredWorkDay(
                                                                null
                                                            )
                                                        }
                                                    >
                                                        {formatWorkDayTime(
                                                            workDay.work_day_time
                                                        )}
                                                    </div>
                                                    <WorkDayTime
                                                        workDay={workDay}
                                                        isVisible={
                                                            hoveredWorkDay ===
                                                            workDay.work_day_id
                                                        }
                                                    />
                                                </div>
                                            )}
                                        {workDay.work_day_in &&
                                            workDay.work_day_out && (
                                                <div className="text-[12px] mt-1">
                                                    <div className="flex justify-between">
                                                        <div className="text-center">
                                                            <div className="text-[#9A9A9A]">
                                                                {t('day-in')}
                                                            </div>
                                                            <div className="font-medium">
                                                                {
                                                                    workDay.work_day_in
                                                                }
                                                            </div>
                                                        </div>
                                                        <div className="text-center">
                                                            <div className="text-[#9A9A9A]">
                                                                {t('day-out')}
                                                            </div>
                                                            <div className="font-medium">
                                                                {
                                                                    workDay.work_day_out
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {getLateMinutesForWorkDay(
                                                        workDay
                                                    ) > 0 && (
                                                        <div className="text-error text-[10px] mt-1">
                                                            {t('late')}:{' '}
                                                            {formatLateMinutes(
                                                                getLateMinutesForWorkDay(
                                                                    workDay
                                                                )
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        {/* Pending day-off indicator */}
                                        {workDay.related_day_offs?.some(
                                            (d) =>
                                                d.day_off_status !== 'approved'
                                        ) && (
                                            <div className="flex items-center gap-0.5 mt-0.5 justify-center">
                                                <SnowshoeingIcons className="w-3 h-3 text-amber-500" />
                                                <span className="text-[9px] text-amber-600">
                                                    Chờ duyệt
                                                </span>
                                            </div>
                                        )}
                                        {/* Pending business indicator */}
                                        {workDay.related_businesses?.some(
                                            (b) =>
                                                b.business_status !== 'approved'
                                        ) && (
                                            <div className="flex items-center gap-0.5 mt-0.5 justify-center">
                                                <BriefCaseIcons className="w-3 h-3 text-amber-500" />
                                                <span className="text-[9px] text-amber-600">
                                                    Chờ duyệt
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {day.day() !== 0 && !workDay && (
                                    <div className="text-xs text-red-500 mt-1">
                                        {}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default WorkDayEmployee
